import NiceModal from '@ebay/nice-modal-react'
import { standardSchemaResolver } from '@hookform/resolvers/standard-schema'
import ErrorOutlinedIcon from '@mui/icons-material/ErrorOutlined'
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined'
import {
  Avatar,
  Box,
  Button,
  Checkbox,
  Container,
  Divider,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Skeleton,
  Stack,
  TextField,
  Typography
} from '@mui/material'
import {
  UpdateCompanyForm,
  updateCompanyFormSchema
} from '@shared/dto/companies.dto'
import { CompanyType } from '@shared/types/companies.types'
import { useEffect, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router'
import ContainedAvatar from '../../components/ContainedAvatar'
import { LoaderButton } from '../../components/LoaderButton'
import { DeleteCompanyModal } from '../../components/modals/DeleteCompanyModal'
import SubHeaderProfile from '../../components/SubHeaderProfile'
import { useUpdateCompanyMutation } from '../../hooks/useMutations/useCompaniesMutations'
import { useCompany } from '../../hooks/useQueries/useCompanies'
import { useSnackbar } from '../../hooks/useSnackbar'
import { getAvatarCompanyName } from '../../utils/company.utils'
import { spanishProvinces } from '../../utils/constants.utils'

export default function CompaniesProfile() {
  const { t } = useTranslation()
  const params = useParams()
  const [hasCheckedDeleteAccount, setHasCheckedDeleteAccount] = useState(false)
  const [logoError, setLogoError] = useState(false)
  const companyId = params.companyId!
  const { data: company, isLoading } = useCompany(companyId)

  const { showSnackbar } = useSnackbar()

  const updateCompanyMutation = useUpdateCompanyMutation(companyId)

  const {
    control,
    watch,
    setValue,
    handleSubmit,
    reset,
    formState: { isDirty, isValid }
  } = useForm<UpdateCompanyForm>({
    defaultValues: {
      name: '',
      tin: '',
      type: CompanyType.CONSTRUCTOR,
      address: '',
      zipcode: '',
      country: '',
      state: '',
      webUrl: '',
      logoUri: null
    },
    resolver: standardSchemaResolver(updateCompanyFormSchema),
    mode: 'onBlur'
  })

  const logoUri = watch('logoUri')

  useEffect(() => {
    if (company) {
      reset({
        name: company.name,
        tin: company.tin,
        type: company.type,
        address: company.address || '',
        zipcode: company.zipcode || '',
        country: company.country || '',
        state: company.state || '',
        webUrl: company.webUrl || '',
        logoUri: company.logoUri
      })
    }
  }, [company, reset])

  const onSubmit = async (data: UpdateCompanyForm) => {
    updateCompanyMutation.mutate(data, {
      onSuccess: () => {
        showSnackbar({
          message: t('edit_company.success'),
          severity: 'success'
        })
      },
      onError: () => {
        showSnackbar({
          message: t('edit_company.error'),
          severity: 'error'
        })
      }
    })
  }

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg']
    if (!allowedTypes.includes(file.type)) {
      setLogoError(true)
      return
    }

    if (file.size > 1024 * 1024 * 3) {
      setLogoError(true)
      return
    }

    setLogoError(false)

    const reader = new FileReader()
    reader.onloadend = () => {
      const base64 = reader.result as string
      setValue('logoUri', base64, { shouldDirty: true })
    }
    reader.readAsDataURL(file)
  }

  if (isLoading) {
    return (
      <>
        <Box
          sx={{
            backgroundColor: 'primary.main',
            width: '100%',
            minHeight: '4.5rem',
            display: 'flex',
            alignItems: 'center',
            mb: '3rem'
          }}
        >
          <Container maxWidth='xl' disableGutters>
            <Stack
              direction='row'
              justifyContent='space-between'
              alignItems='center'
              sx={{ width: '100%' }}
            >
              <Skeleton
                variant='rectangular'
                width='18.75rem'
                height='2.25rem'
                sx={{ bgcolor: 'rgba(255,255,255,0.3)' }}
              />
              <Skeleton
                variant='rectangular'
                width='5rem'
                height='2.25rem'
                sx={{ bgcolor: 'rgba(255,255,255,0.3)' }}
              />
            </Stack>
          </Container>
        </Box>

        <Container maxWidth='xl' disableGutters>
          <Box display='flex' justifyContent='center' marginTop={'3rem'}>
            <Box width='85%'>
              <Box display='flex' justifyContent='flex-end'>
                <Skeleton variant='rectangular' width='7.5rem' height='2rem' />
              </Box>

              <Box display='flex' justifyContent='space-between' py={'1.5rem'}>
                <Box display='flex' flexDirection='column' gap='1rem'>
                  <Skeleton variant='text' width='9.375rem' height='2rem' />
                  <Skeleton variant='text' width='15.625rem' height='1.5rem' />
                </Box>
                <Box
                  display='flex'
                  flexDirection='row'
                  gap='1.5rem'
                  width='30rem'
                >
                  <Skeleton variant='rounded' width='6.5rem' height='4.5rem' />
                  <Box
                    display='flex'
                    flexDirection='column'
                    alignItems='flex-start'
                  >
                    <Skeleton
                      variant='rectangular'
                      width='7.5rem'
                      height='2rem'
                    />
                    <Skeleton variant='text' width='12.5rem' height='1rem' />
                    <Skeleton
                      variant='rectangular'
                      width='5rem'
                      height='1.5rem'
                    />
                  </Box>
                </Box>
              </Box>
              <Divider />

              <Box display='flex' justifyContent='space-between' py={'2rem'}>
                <Box display='flex' flexDirection='column' gap='1rem'>
                  <Skeleton variant='text' width='12.5rem' height='2rem' />
                  <Skeleton variant='text' width='18.75rem' height='1.5rem' />
                </Box>
                <Box
                  display='flex'
                  flexDirection='column'
                  gap={2}
                  width='30rem'
                >
                  <Skeleton
                    variant='rectangular'
                    width='100%'
                    height='3.5rem'
                  />
                  <Skeleton
                    variant='rectangular'
                    width='100%'
                    height='3.5rem'
                  />
                  <Skeleton
                    variant='rectangular'
                    width='100%'
                    height='3.5rem'
                  />
                </Box>
              </Box>
              <Divider />

              <Box display='flex' justifyContent='space-between' py={'2rem'}>
                <Box display='flex' flexDirection='column' gap='1rem'>
                  <Skeleton variant='text' width='11.25rem' height='2rem' />
                  <Skeleton variant='text' width='17.5rem' height='1.5rem' />
                </Box>
                <Box
                  display='flex'
                  flexDirection='column'
                  gap={2}
                  width='30rem'
                >
                  <Skeleton
                    variant='rectangular'
                    width='100%'
                    height='3.5rem'
                  />
                  <Skeleton
                    variant='rectangular'
                    width='100%'
                    height='3.5rem'
                  />
                  <Skeleton
                    variant='rectangular'
                    width='100%'
                    height='3.5rem'
                  />
                  <Skeleton
                    variant='rectangular'
                    width='100%'
                    height='3.5rem'
                  />
                </Box>
              </Box>
              <Divider />

              <Box display='flex' justifyContent='space-between' py={'2rem'}>
                <Box display='flex' flexDirection='column' gap='1rem'>
                  <Skeleton variant='text' width='7.5rem' height='2rem' />
                  <Skeleton variant='text' width='13.75rem' height='1.5rem' />
                </Box>
                <Box
                  display='flex'
                  flexDirection='column'
                  gap={2}
                  width='30rem'
                >
                  <Skeleton
                    variant='rectangular'
                    width='100%'
                    height='3.5rem'
                  />
                </Box>
              </Box>
              <Divider />

              <Box display='flex' justifyContent='space-between' py={'2rem'}>
                <Box display='flex' flexDirection='column' gap='1rem'>
                  <Skeleton variant='text' width='10rem' height='2rem' />
                  <Skeleton variant='text' width='16.25rem' height='1.5rem' />
                </Box>
                <Box
                  display='flex'
                  flexDirection='column'
                  gap={2}
                  width='30rem'
                >
                  <Skeleton variant='text' width='100%' height='1.5rem' />
                  <Skeleton
                    variant='rectangular'
                    width='12.5rem'
                    height='1.5rem'
                  />
                  <Skeleton
                    variant='rectangular'
                    width='7.5rem'
                    height='2rem'
                  />
                </Box>
              </Box>
            </Box>
          </Box>
        </Container>
      </>
    )
  }

  return (
    <>
      <SubHeaderProfile
        title={company?.name || ''}
        subtitle={t('companies_profile.header_subtitle')}
        isDirty={isDirty}
      />
      <Container maxWidth='xl' disableGutters>
        <Box display='flex' justifyContent='center' marginTop={'3rem'}>
          <Box width='85%'>
            <Box display='flex' justifyContent='flex-end'>
              <LoaderButton
                variant='contained'
                onClick={handleSubmit(onSubmit)}
                type='submit'
                sx={{
                  padding: '0.25rem 0.75rem',
                  fontSize: '0.75rem',
                  width: 'fit-content',
                  textTransform: 'capitalize',
                  color: 'black'
                }}
                label={t('user.save_changes')}
                disabled={
                  updateCompanyMutation.isPending || !isValid || !isDirty
                }
                loading={updateCompanyMutation.isPending}
              />
            </Box>

            <Box display='flex' justifyContent='space-between' py={'1.5rem'}>
              <Box display='flex' flexDirection='column' gap='1rem'>
                <Typography variant='h5' fontWeight='bold'>
                  {t('companies_profile.image')}
                </Typography>
                <Typography variant='body1'>
                  {t('companies_profile.image_subtitle')}
                </Typography>
              </Box>
              <Box
                display='flex'
                flexDirection='row'
                gap='1.5rem'
                width='30rem'
              >
                {logoUri ? (
                  <ContainedAvatar
                    src={logoUri}
                    sx={{ width: '6.5rem', height: '4.5rem' }}
                    variant='rounded'
                  />
                ) : (
                  <Avatar
                    sx={{ width: '4.5rem', height: '4.5rem' }}
                    variant='rounded'
                  >
                    {company?.name ? getAvatarCompanyName(company.name) : ''}
                  </Avatar>
                )}
                <Box
                  display='flex'
                  flexDirection='column'
                  alignItems='flex-start'
                >
                  <Button
                    component='label'
                    variant='text'
                    startIcon={<UploadFileOutlinedIcon />}
                    sx={{
                      textTransform: 'capitalize'
                    }}
                  >
                    {t('user.change_image')}
                    <input
                      type='file'
                      accept='image/png, image/jpeg, image/jpg'
                      hidden
                      onChange={handleFileChange}
                    />
                  </Button>
                  {logoError && (
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                        padding: '0.563rem',
                        visibility: 'visible'
                      }}
                    >
                      <ErrorOutlinedIcon
                        sx={{ color: 'error.main', fontSize: '1.25rem' }}
                      />
                      <Typography
                        variant='caption'
                        sx={{ color: 'error.main' }}
                      >
                        {t('companies_profile.image_error')}
                      </Typography>
                    </Box>
                  )}
                  <Typography
                    variant='caption'
                    sx={{ paddingLeft: '0.4rem', color: 'text.secondary' }}
                  >
                    {t('profile.change_image')}
                  </Typography>
                  <Button
                    variant='text'
                    size='small'
                    onClick={() => {
                      setValue('logoUri', '', { shouldDirty: true })
                    }}
                    disabled={!logoUri}
                    sx={{
                      color: 'secondary.main',
                      textTransform: 'none',
                      fontSize: '0.81rem'
                    }}
                  >
                    {t('profile.delete_image')}
                  </Button>
                </Box>
              </Box>
            </Box>
            <Divider />

            <Box display='flex' justifyContent='space-between' py={'2rem'}>
              <Box display='flex' flexDirection='column' gap='1rem'>
                <Typography variant='h5' fontWeight='bold'>
                  {t('companies_profile.info_title')}
                </Typography>
                <Typography variant='body1'>
                  {t('companies_profile.info_subtitle')}
                </Typography>
              </Box>
              <Box display='flex' flexDirection='column' gap={2} width='30rem'>
                <Controller
                  name='name'
                  control={control}
                  render={({ field, fieldState: { error } }) => (
                    <TextField
                      {...field}
                      variant='filled'
                      label={t('edit_company.form.name')}
                      fullWidth
                      error={!!error}
                      helperText={error?.message ? t(error.message) : ''}
                    />
                  )}
                />
                <Controller
                  name='tin'
                  control={control}
                  render={({ field, fieldState: { error } }) => (
                    <TextField
                      {...field}
                      variant='filled'
                      label={t('edit_company.form.tin')}
                      fullWidth
                      error={!!error}
                      helperText={error?.message ? t(error.message) : ''}
                    />
                  )}
                />
                <Controller
                  name='type'
                  control={control}
                  render={({ field }) => (
                    <FormControl fullWidth variant='filled'>
                      <InputLabel>{t('edit_company.form.type')}</InputLabel>
                      <Select
                        {...field}
                        fullWidth
                        label={t('edit_company.form.type')}
                        variant='filled'
                      >
                        <MenuItem value={CompanyType.CONSTRUCTOR}>
                          {t('common.constructor_type')}
                        </MenuItem>
                        <MenuItem value={CompanyType.PROVIDER}>
                          {t('common.provider')}
                        </MenuItem>
                      </Select>
                    </FormControl>
                  )}
                />
              </Box>
            </Box>
            <Divider />

            <Box display='flex' justifyContent='space-between' py={'2rem'}>
              <Box display='flex' flexDirection='column' gap='1rem'>
                <Typography variant='h5' fontWeight='bold'>
                  {t('companies_profile.direction_title')}
                </Typography>
                <Typography variant='body1'>
                  {t('companies_profile.direction_subtitle')}
                </Typography>
              </Box>
              <Box display='flex' flexDirection='column' gap={2} width='30rem'>
                <Controller
                  name='address'
                  control={control}
                  render={({ field, fieldState: { error } }) => (
                    <TextField
                      {...field}
                      variant='filled'
                      fullWidth
                      label={t('edit_company.form.address')}
                      error={!!error}
                      helperText={error?.message}
                    />
                  )}
                />
                <Controller
                  name='country'
                  control={control}
                  render={({ field, fieldState: { error } }) => (
                    <FormControl fullWidth error={!!error} variant='filled'>
                      <InputLabel>{t('edit_company.form.country')}</InputLabel>
                      <Select
                        {...field}
                        label={t('edit_company.form.country')}
                        variant='filled'
                      >
                        <MenuItem
                          key={t('common.spain')}
                          value={t('common.spain')}
                        >
                          {t('common.spain')}
                        </MenuItem>
                      </Select>
                    </FormControl>
                  )}
                />
                <Controller
                  name='state'
                  control={control}
                  render={({ field, fieldState: { error } }) => (
                    <FormControl fullWidth error={!!error} variant='filled'>
                      <InputLabel>{t('edit_company.form.state')}</InputLabel>
                      <Select
                        {...field}
                        label={t('edit_company.form.state')}
                        variant='filled'
                      >
                        {spanishProvinces.map(province => (
                          <MenuItem key={province} value={province}>
                            {province}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                />
                <Controller
                  name='zipcode'
                  control={control}
                  render={({ field, fieldState: { error } }) => (
                    <TextField
                      {...field}
                      variant='filled'
                      fullWidth
                      label={t('edit_company.form.zip_code')}
                      error={!!error}
                      helperText={error?.message ? t(error.message) : ''}
                    />
                  )}
                />
              </Box>
            </Box>
            <Divider />

            <Box display='flex' justifyContent='space-between' py={'2rem'}>
              <Box display='flex' flexDirection='column' gap='1rem'>
                <Typography variant='h5' fontWeight='bold'>
                  {t('companies_profile.web_uri_title')}
                </Typography>
                <Typography variant='body1'>
                  {t('companies_profile.web_uri_subtitle')}
                </Typography>
              </Box>
              <Box display='flex' flexDirection='column' gap={2} width='30rem'>
                <Controller
                  name='webUrl'
                  control={control}
                  render={({ field, fieldState: { error } }) => (
                    <TextField
                      {...field}
                      variant='filled'
                      label={t('edit_company.form.web_url')}
                      fullWidth
                      error={!!error}
                      helperText={
                        error?.message ||
                        t('edit_company.form.web_url_helper_text')
                      }
                    />
                  )}
                />
              </Box>
            </Box>
            <Divider />

            <Box display='flex' justifyContent='space-between' py={'2rem'}>
              <Box display='flex' flexDirection='column' gap='1rem'>
                <Typography variant='h5' fontWeight='bold'>
                  {t('companies_profile.delete_company_title')}
                </Typography>
                <Typography variant='body1'>
                  {t('companies_profile.delete_company_subtitle')}
                </Typography>
              </Box>
              <Box display='flex' flexDirection='column' gap={2} width='30rem'>
                <Typography variant='body2' fontWeight='bold'>
                  {t('companies_profile.delete_company_description')}
                </Typography>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={hasCheckedDeleteAccount}
                      onChange={e =>
                        setHasCheckedDeleteAccount(e.target.checked)
                      }
                    />
                  }
                  label={t('companies_profile.delete_company_check')}
                />
                <Button
                  onClick={() =>
                    NiceModal.show(DeleteCompanyModal, {
                      companyId: companyId,
                      companyName: company?.name
                    })
                  }
                  size='small'
                  sx={{ width: 'fit-content', textTransform: 'none' }}
                  variant='contained'
                  disabled={!hasCheckedDeleteAccount}
                  color='error'
                >
                  {t('user.delete_company')}
                </Button>
              </Box>
            </Box>
          </Box>
        </Box>
      </Container>
    </>
  )
}
